import React, { useEffect, useState, useCallback } from 'react';
import { Button, Input, Alert, Switch, Tooltip, Spin, message } from 'antd';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { SearchOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import useMedicationSetting from 'hooks/useMedicationSetting';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import cls from 'classnames';
import MedicationCard from './components/MedicationCard';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { saveMedicationSetting } from 'api/common';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import KlarityLogo from 'assets/logo/klarity.png';
import { shouldUseKlarityUi } from 'utils/common';
import AIChat from 'components/AIChat';
import { EAIChatPageId } from 'types/ai';

const MedicationSettingsPage = () => {
    const navigate = useNavigate();
    const [data, loading] = useMedicationSetting();
    const [settings] = useSettings();
    const stateList = settings[EnumFields.STATE] || [];
    const [selectedState, setSelectedState] = useState<string>();
    const [stateNonMedications, setStateNonMedications] = useState<Record<string, string[]>>({});
    const [openedCard, setOpenedCard] = useState<string>();
    const [syncAll, setSyncAll] = useState(false);
    const [searchVal, setSearchVal] = useState('');
    const [saving, setSaving] = useState(false);
    const [homeInfo, loadingHomeInfo, refetchHomeInfo] = useProviderHomeInfo();
    const showKlarityUi = shouldUseKlarityUi(homeInfo);

    useEffect(() => {
        if (data.stateNonPrescribableMedications) {
            setStateNonMedications(data.stateNonPrescribableMedications);
            setSelectedState(Object.keys(data.stateNonPrescribableMedications)[0]);
        } else {
            setSelectedState(Object.keys(data.stateConditions || {})[0]);
        }
        if (data.syncAllState) {
            setSyncAll(true);
            setSelectedState(data.syncAllState);
            const newStateNonMedications = { ...data.stateNonPrescribableMedications };
            const currentNonMedications = data.stateNonPrescribableMedications?.[data.syncAllState];
            Object.keys(data.stateConditions || {}).forEach((state) => {
                if (state === data.syncAllState) return;
                const medicationsInTargetState = data.stateConditions?.[state].reduce((acc, item) => {
                    acc.push(...data.conditionMedications?.[item] || []);
                    return acc;
                }, [] as string[]);
                newStateNonMedications[state] = currentNonMedications?.filter((med) => medicationsInTargetState?.includes(med)) || [];
            });
            setStateNonMedications(newStateNonMedications);
        }
    }, [data]);

    const onStatesChange = (state: string) => {
        setSelectedState(state);
        if (!stateNonMedications[state]) {
            setStateNonMedications({
                ...stateNonMedications,
                [state]: [],
            });
        }
    };

    const changeStateNonMedicaitons = (checkedValues: string[], unCheckedValues: string[]) => {
        if (!selectedState) return;
        setStateNonMedications({
            ...stateNonMedications,
            [selectedState]: Array.from(new Set([...stateNonMedications[selectedState] || [], ...checkedValues])).filter((item) => !unCheckedValues.includes(item)),
        });
    };

    const toggleCard = useCallback((condition: string) => {
        setOpenedCard((prev) => (prev === condition ? undefined : condition));
    }, []);

    const onSyncAllChange = (value: boolean) => {
        if (value) {
            if (!selectedState) return;
            const currentNonMedications = stateNonMedications[selectedState] || [];
            const newStateNonMedications = { ...stateNonMedications };
            Object.keys(data.stateConditions || {}).forEach((state) => {
                if (state === selectedState) return;
                const medicationsInTargetState = data.stateConditions?.[state].reduce((acc, item) => {
                    acc.push(...data.conditionMedications?.[item] || []);
                    return acc;
                }, [] as string[]);
                newStateNonMedications[state] = currentNonMedications.filter((med) => medicationsInTargetState?.includes(med));
            });
            setStateNonMedications(newStateNonMedications);
            setSyncAll(true);
        } else {
            setSyncAll(false);
        }
    };

    const onSave = async () => {
        if (!selectedState) return;
        const params = syncAll ? [{
            state: selectedState,
            nonPrescribableMedications: stateNonMedications[selectedState],
            syncAllState: true,
        }] : Object.entries(stateNonMedications).map(([state, medications]) => ({
            state,
            nonPrescribableMedications: medications,
            syncAllState: false,
        }));
        setSaving(true);
        try {
            const res = await saveMedicationSetting(params);
            if (!res.error) {
                navigate(PATH.MEDICATION_SETTINGS_SUCCESS);
            }
        } catch (error) {
            message.error('Save failed. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleExit = useCallback(() => {
        // 检查是否有 referrer 或者是否是直接访问
        const hasReferrer = document.referrer &&
            document.referrer !== window.location.href &&
            new URL(document.referrer).origin === window.location.origin;

        if (hasReferrer) {
            navigate(-1);
        } else {
            navigate(PATH.DASHBOARD, { replace: true });
        }
    }, []);

    const handleSearchMedicaiton = (con: string) => {
        if (!searchVal) {
            return data.conditionMedications?.[con] || [];
        }
        const searchedMedications = data.conditionMedications?.[con].filter((med) => med.toLowerCase().includes(searchVal.toLowerCase())) || [];
        return searchedMedications.length > 0 ? searchedMedications : data.conditionMedications?.[con] || [];
    };

    const states = data?.stateConditions ? Object.keys(data.stateConditions).map((item) => stateList.find((state) => state.value === item)!) : [];
    const conditions = Object.keys(data.conditionMedications || {});
    const allMedications = Array.from(new Set(Object.values(data.conditionMedications || {}).flat()));
    let stateConditions = selectedState ? data.stateConditions?.[selectedState]?.filter((item) => conditions.includes(item)) || [] : [];
    const nonPrescribeMedications = selectedState ? stateNonMedications?.[selectedState] || [] : [];
    const otherMedicaitons = nonPrescribeMedications.filter((item) => !allMedications.includes(item));

    if (searchVal) {
        const lowerSearchVal = searchVal.toLowerCase();
        stateConditions = stateConditions.filter((item) => {
            if (data.conditionMedications) {
                const medications = data.conditionMedications[item];
                if (medications.some((med) => med.toLowerCase().includes(lowerSearchVal))) {
                    return true;
                }
            }
            return item.toLowerCase().includes(lowerSearchVal);
        });
    }
    const hideOtherCard = searchVal && stateConditions.length > 0;
    const noResult = searchVal && stateConditions.length === 0;

    let title = 'Customize your medication visibility';
    let desc = 'Based on the conditions you treat, you’re automatically linked to relevant medications. These settings help Klarity present accurate information to patients.';
    if (showKlarityUi) {
        title = 'Manage your visibility for medications shown on Klarity';
        desc = 'Based on the conditions you treat, you’re automatically linked to relevant medications. These may also be highlighted in Klarity’s marketing content to help patients find the right care. You can opt out of any medications you don’t prescribe or prefer not to include, and manually add any missing ones under “Others.”';
    }

    useEffect(() => {
        if (noResult) {
            setOpenedCard('Other');
        } else {
            setOpenedCard(undefined);
        }
    }, [noResult]);

    return (
        <div className={`${s.wrap} ${showKlarityUi ? s.wrapKlarity : ''}`}>
            <div className={s.header}>
                <div
                    className={s.logoWrap}
                    onClick={() => {
                        navigate(PATH.DASHBOARD);
                    }}
                >
                    {
                        !showKlarityUi &&
                        <img
                            src="https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/ui/logo_kiwihealth_by_klarity_380x96.png"
                            className={s.logo}
                            alt="Kiwi Health Logo"
                        />
                    }
                    {
                        showKlarityUi &&
                        <img
                            src={KlarityLogo}
                            className={s.logoKlarity}
                            alt="Klarity Logo"
                        />
                    }

                </div>
            </div>

            <div className={s.content}>
                <h2 className={s.title}>{title}</h2>
                <div className={s.desc}>
                    {desc}
                </div>

                <Alert
                    type="info"
                    showIcon
                    className={s.alert}
                    message={
                        <span>
                            Medications you choose not to prescribe will be shown on your profile. This helps patients avoid booking mistakes and improves matching.
                        </span>
                    }
                />

                <Spin spinning={loading}>
                    <div className={s.settings}>
                        <Input
                            value={searchVal}
                            placeholder="Search condition or medication"
                            className={s.search}
                            prefix={<SearchOutlined style={{ color: '#6B7280' }} />}
                            allowClear
                            onChange={(e) => setSearchVal(e.target.value)}
                        />
                        {noResult && <div className={s.noResult}>No results found. You can add it manually under “Others” below.</div>}
                        <div className={s.statesRow}>
                            <div className={s.statesList}>
                                {states.length > 0 && states.map((state) => (
                                    <Button
                                        key={state.value}
                                        className={cls(s.stateBtn, { [s.active]: selectedState === state.value })}
                                        onClick={() => onStatesChange(state.value as string)}
                                        disabled={syncAll && selectedState !== state.value}
                                    >
                                        {state.label}
                                    </Button>
                                ))}
                            </div>
                            <div className={s.syncAll}>
                                <span className={s.syncAllText}>Sync across all states</span>
                                <Tooltip title="Sync your medication settings across all states.">
                                    <QuestionCircleOutlined style={{ color: '#BDBDBD', marginRight: 8 }} />
                                </Tooltip>
                                <Switch checked={syncAll} onChange={onSyncAllChange} size="small" />
                            </div>
                        </div>

                        {stateConditions.map((item, idx) => (
                            <MedicationCard
                                key={idx}
                                medications={handleSearchMedicaiton(item)}
                                condition={item}
                                nonPrescribeMedications={nonPrescribeMedications}
                                onChange={changeStateNonMedicaitons}
                                isOpen={openedCard === item}
                                onToggle={() => toggleCard(item)}
                            />
                        ))}
                        {!hideOtherCard && <MedicationCard
                            condition="Other"
                            nonPrescribeMedications={otherMedicaitons}
                            onChange={changeStateNonMedicaitons}
                            isOpen={openedCard === 'Other'}
                            onToggle={() => toggleCard('Other')}
                        />}
                    </div>
                </Spin>
            </div>

            <div className={s.footer}>
                <div className={s.btns} onClick={handleExit}>
                    <Button>Exit</Button>
                </div>
                <Button type="primary" className={s.exitButton} onClick={onSave} loading={saving}>
                    Save
                </Button>
            </div>
            <div className={commonS.chatWrap}>
                <AIChat
                    pageId={EAIChatPageId.PROVIDER_MEDICATION}
                    defaultShow={false}
                    makeFix={false}
                />
            </div>
        </div>
    );
};

export default MedicationSettingsPage;
